import { ApplicationRef, Injectable, NgZone, inject } from '@angular/core'
import { Router } from '@angular/router'
import { BehaviorSubject, Subject, filter, mergeMap, take, tap } from 'rxjs'

import { User } from '@app/interfaces/user'
import { SignalService } from '@app/services/signal.service'
import { UserService } from '@app/services/user.service'

import { FakeDataSubjectPayload, TourStep } from './tour'
import { TourKey } from './tour.constants'

@Injectable({ providedIn: 'root' })
export class TourService {
  public readonly router = inject(Router)
  private readonly userService = inject(UserService)
  private readonly signalService = inject(SignalService)
  private readonly ngZone = inject(NgZone)
  private readonly appRef = inject(ApplicationRef)

  public currentIndex: number = 0

  public currentKeySubject = new BehaviorSubject<TourStep>({ uniqueKey: '' })
  private readonly fakeDataSubject = new Subject<FakeDataSubjectPayload>()

  private user?: User
  private readonly localStorageMarker = 'coreDidTourV0'
  private isCurrentlyShowing = false

  public filteredSteps: TourStep[] = []

  public nullStep: TourStep = {
    uniqueKey: '',
    tourKeys: [TourKey.NONE],
    heading: '',
  }

  public allSteps: TourStep[] = []

  constructor () {
    if (!this.userService?.getUser) return

    // listen for the user to login, and then launch the tour is the user hasn't already seen it
    this.userService.getUser()
      .pipe(
        tap(user => { this.user = user }),
        filter(user => !user.anonymous), // Filter out anonymous users.
        mergeMap(_ => this.appRef.isStable), // Listen to app status
        take(1), // Wait for the first time the app is stable
      ).subscribe(() => {
        setTimeout(() => {
          this.startIfNotDone(TourKey.CORE)
          this.startIfNotDone(TourKey.CORE_V1)
        }, 1000)
      })
  }

  broadcastNewStep () {
    return this.currentKeySubject.asObservable()
  }

  broadcastFakeData () {
    return this.fakeDataSubject.asObservable()
  }

  /**
   * Potentially start the tour for a given key if the user hasn't already done it
   */
  startIfNotDone (tourKey: TourKey) {
    if (!localStorage.getItem(tourKey)) {
      this.start(tourKey)
    }
  }

  start (tourKey?: TourKey) {
    // refuse to do anything is the user isn't logged in
    if (!this.user || this.user.anonymous) return
    // if the tour is already showing a step, refuse to go any further
    if (this.isCurrentlyShowing) return
    // assume all steps
    if (!tourKey) {
      this.filteredSteps = structuredClone(this.allSteps)
    } else {
      // if we're given a filter, filter the tour using that value
      this.filteredSteps = this.allSteps.filter((step: TourStep) => {
        return step.tourKeys?.includes(tourKey)
      })
    }
    // reset the tour to the first step
    this.currentIndex = 0

    if (this.filteredSteps.length === 0) {
      return
    }
    // show the first step
    this.ngZone.run(() => this.showStep(this.currentIndex))
  }

  stop () {
    this.setNotShowing()
    this.start(TourKey.NONE)
  }

  previousStep () {
    if (this.currentIndex <= 0) return
    this.currentIndex--
    const step = this.filteredSteps[this.currentIndex]

    if (!step.permissions || this.userService.hasPermissionIn(step.permissions)) {
      this.showStep(this.currentIndex)
    } else {
      this.previousStep()
    }
  }

  nextStep () {
    // while we are setting up the next step we set a temporary 'null step' to avoid multiple steps displaying / flashing
    this.currentKeySubject.next(this.nullStep)

    if (this.currentIndex >= this.filteredSteps.length - 1) {
      // if we have gone past the last step, call an explicit stop() to prevent the last step from re-displaying
      this.stop()
      return
    }

    this.currentIndex++
    const step = this.filteredSteps[this.currentIndex]

    if (!step.permissions || this.userService.hasPermissionIn(step.permissions)) {
      this.showStep(this.currentIndex)
    } else {
      this.nextStep()
    }
  }

  private showStep (index: number) {
    const step = this.filteredSteps[index]
    // potentially navigate to new url
    let promise = new Promise<boolean>((resolve) => { return resolve(true) })
    if (step.url) {
      promise = this.router.navigate([step.url], step.urlExtras)
    }

    // if / once we're on the correct URL..
    promise.then(() => {
      // wait in a timeout for the various TourComponents to be initialised
      setTimeout(() => {
        if (step.fakeData) {
          // If we have fake data to emit, then prepare a payload for the fake data subject, in the form of a Promise
          //   which will be resolved then the fake data has been applied
          const fakeDataPromise = new Promise<void>((resolve) => {
            // broadcast the subject containing the fake data and the callback to the generic detail component
            this.fakeDataSubject.next({
              data: step.fakeData,
              callback: resolve,
            })
          })
          // Listen for the callback to be called (which will resolve the Promise),
          //   and inform the TourComponet that it is ready by advancing to the next Tour step.
          // This must be done in a setTimeout to allow any ngIf etc rules to be applied following the inject of fake data
          fakeDataPromise.then(() => {
            setTimeout(() => {
              this.currentKeySubject.next(step)
            }, 10)
          })
        }
        if (step.signalKey) {
          setTimeout(() => {
            this.signalService.sendSignal(step.signalKey!)
          })
        }

        if (!step.fakeData) {
          // there was no fake data, broadcast the next step directly
          setTimeout(() => {
            this.currentKeySubject.next(step)
          })
        }
      })
    });

    // mark the user as having seen this tour key
    (step.tourKeys ?? []).forEach((key) => {
      localStorage.setItem(key ?? this.localStorageMarker, 'true')
    })
    // set the tour as showing a step, which will prevent other requests to start
    // a tour while this step is visible
    this.isCurrentlyShowing = true
  }

  /**
   * TourComponent can inform us that a step is no longer showing: used to determine if we can start a new tour or not
   */
  setNotShowing () {
    this.isCurrentlyShowing = false
  }
}
